<script lang="ts" setup generic="T extends PDataTableRow">
import { QTable, QLinearProgress, QTd, QTh } from 'quasar'
import { computed, ref, useSlots, watch, toRef } from 'vue'
import iconSet from 'quasar/icon-set/fontawesome-v6'
import { Color } from '@previo/codebooks'
import { $t } from '../../composables/i18n'
import PBtnTable from '../btn-table/PBtnTable.vue'
import PCheckbox from '../fields/PCheckbox.vue'
import PDivider from '../divider/PDivider.vue'
import { PMaxLines } from '../typography/types'
import PReadOnlyField from '../fields/PReadOnlyField.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextStandard from '../typography/body/PTextStandard.vue'
import PTextFormBody from '../typography/form/PTextFormBody.vue'
import PTextFormLabel from '../typography/form/PTextFormLabel.vue'
import { usePrevioDS } from '../../composables/helpers'
import {
  type PDataTablePaginationType,
  type PDataTableColumnName,
  type PDataTableColumn,
  type PDataTableRequest,
  type PDataTableRow,
  type PDataTableRowBackgroundColorFn,
  type PDataTableRowIdValue,
  type PDataTableRowKey,
  PDataTableSeparator,
  SortDir,
} from './types'
import PDataTablePagination from './PDataTablePagination.vue'
import { usePersistedColumns } from './persistence/columns'
import DataTableVisibleColumnsContextMenu from './components/DataTableVisibleColumnsContextMenu.vue'
import { useClientSort } from './composables/clientSort'
import { useColumnResize } from './composables/columnResize'
import {
  type ScrollableItem,
  useMobileDataTableScrollSync,
} from './composables/useMobileDataTableScrollSync'
import { DEFAULT_HIGHLIGHT_DURATION, useRowHighlight } from './composables/useRowHighlight'
import { useSelection } from './composables/useSelection'
import DataTableItemMobile from './components/DataTableItemMobile.vue'
import DataTableColumnResizeHandle from './components/DataTableColumnResizeHandle.vue'
import {
  type TQDataTableBody,
  type TQDataTableHeader,
  type TQDataTablePagination,
  type TQDataTableRequest,
} from './qTypes'

// Change sorting icons
iconSet.table.arrowUp = 'fa fa-light fa-chevron-up'
iconSet.arrow.dropdown = 'fa fa-light fa-chevron-down'

type Props = {
  columns: PDataTableColumn[]
  items: T[]
  rowKey: PDataTableRowKey
  noData?: string
  loading?: boolean
  selectable?: boolean
  selectableChildren?: boolean
  stickyFirstColumn?: boolean
  stickyLastColumn?: boolean
  hidePagination?: boolean
  hideHeader?: boolean
  shrink?: boolean
  expandable?: boolean
  expandableItemsKey?: PDataTableRowKey | null
  customExpandRowConnector?: boolean
  isRowExpandable?: ((row: T) => boolean) | null
  isRowSelectable?: ((row: T) => boolean) | null
  disableMobileLayout?: boolean
  disableToggleVisibleColumns?: boolean
  disableClickableRows?: boolean
  separator?: PDataTableSeparator
  enablePersistence?: string
  clientSort?: boolean
  rowBackgroundColor?: PDataTableRowBackgroundColorFn<T>
  highlightedRowIds?: PDataTableRowIdValue[]
  highlightDuration?: number
  disableResize?: boolean
}

const visibleColumns = defineModel<PDataTableColumnName[]>('visibleColumns')
const selectedItems = defineModel<PDataTableRowIdValue[]>('selectedItems', { default: [] })
const selectedChildItems = defineModel<PDataTableRowIdValue[]>('selectedChildItems', {
  default: [],
})
const expandableItems = defineModel<PDataTableRowIdValue[]>('expandableItems', { default: [] })

const pagination = defineModel<PDataTablePaginationType>('pagination', {
  default: {
    page: 1,
    rowsPerPage: 0,
  },
})

const props = withDefaults(defineProps<Props>(), {
  noData: undefined,
  loading: false,
  selectable: false,
  selectableChildren: false,
  stickyFirstColumn: false,
  stickyLastColumn: false,
  hidePagination: false,
  hideHeader: false,
  shrink: false,
  expandable: false,
  expandableItemsKey: null,
  customExpandRowConnector: false,
  isRowExpandable: null,
  isRowSelectable: null,
  disableMobileLayout: false,
  disableToggleVisibleColumns: false,
  disableClickableRows: false,
  separator: PDataTableSeparator.HORIZONTAL,
  enablePersistence: undefined,
  clientSort: false,
  highlightedRowIds: () => [],
  highlightDuration: DEFAULT_HIGHLIGHT_DURATION,
  disableResize: false,
})

type Emits = {
  (e: 'row-click', row: T): void
  (e: 'request', request: PDataTableRequest): void
  (e: 'expand', row: T): void
  (e: 'collapse', row: T): void
}

const emits = defineEmits<Emits>()

const ds = usePrevioDS()
const slots = useSlots()
const itemSlotKey = 'item.'

const mobileScrollSync = useMobileDataTableScrollSync()

const resolveRowBackgroundColor = (
  row: T,
  resultHandler: (color: Color) => string,
): string | null => {
  if (!props.rowBackgroundColor) return null
  const color = props.rowBackgroundColor?.(row)
  if (!color) return null
  return resultHandler(color)
}

const getRowClass = (row: T): string => {
  const rowClass = resolveRowBackgroundColor(row, () => 'row-bg-color')
  return rowClass ?? ''
}

const { isRowIdHighlighted } = useRowHighlight({
  highlightedRowIds: toRef(props, 'highlightedRowIds'),
  highlightDuration: toRef(props, 'highlightDuration'),
})

const isRowHighlighted = (row: T): boolean => {
  return isRowIdHighlighted(row[props.rowKey] as PDataTableRowIdValue)
}

const getRowStyle = (row: T): string => {
  const rowStyle = resolveRowBackgroundColor(row, (color: Color): string => {
    return `background-color: ${ds.useColor().getHexByColor(color)}`
  })
  return rowStyle ?? ''
}

const buildInitialColumnOrder = (): PDataTableColumnName[] => {
  const names = props.columns.map((col) => col.name)

  if (visibleColumns.value && visibleColumns.value.length > 0) {
    const visible = visibleColumns.value.filter((name) => names.includes(name))
    const rest = names.filter((name) => !visible.includes(name))
    return [...visible, ...rest]
  }

  return names
}

const columnOrder = ref<PDataTableColumnName[]>(buildInitialColumnOrder())

const initialVisibleColumns: PDataTableColumnName[] | undefined = visibleColumns.value
  ? [...visibleColumns.value]
  : undefined
const initialColumnOrder: PDataTableColumnName[] = [...columnOrder.value]

const mobileLayoutEnabled = computed<boolean>(() => {
  return !props.disableMobileLayout && ds.screen().isMobile
})

const isColumnHidden = (name: string): boolean => {
  return props.columns.find((col) => col.name === name)?.hidden?.() || false
}

const {
  columnWidths,
  isResizing,
  hasCustomColumnWidths,
  isColumnResizable,
  getResizeTargetName,
  startColumnResize,
  getColumnWidthStyle,
} = useColumnResize({
  columns: toRef(props, 'columns'),
  enabled: computed(() => !props.disableResize && !mobileLayoutEnabled.value),
  isColumnHidden,
})

const syncColumnOrder = (names: PDataTableColumnName[]) => {
  const current = columnOrder.value.length > 0 ? columnOrder.value : buildInitialColumnOrder()
  const kept = current.filter((name) => names.includes(name))
  const added = names.filter((name) => !kept.includes(name))
  columnOrder.value = [...kept, ...added]
}

watch(
  () => props.columns,
  (cols) => syncColumnOrder(cols.map((col) => col.name)),
  { deep: true },
)

watch(columnOrder, (order) => {
  const currentVisible = visibleColumns.value

  if (!currentVisible || currentVisible.length === 0) {
    return
  }

  const orderedVisible = order.filter((name) => currentVisible.includes(name))

  if (orderedVisible.join('|') !== currentVisible.join('|')) {
    visibleColumns.value = orderedVisible
  }
})

const qColumnsData = computed<PDataTableColumn[]>(() => {
  const columnMap = new Map(props.columns.map((col) => [col.name, col]))

  const orderedColumns: PDataTableColumn[] = []

  const order = columnOrder.value.length > 0 ? columnOrder.value : buildInitialColumnOrder()

  order.forEach((colName) => {
    const col = columnMap.get(colName)
    if (col) {
      orderedColumns.push(col)
      columnMap.delete(colName)
    }
  })

  columnMap.forEach((col) => orderedColumns.push(col))

  return orderedColumns
})

const qColumns = computed<PDataTableColumn[]>(() =>
  qColumnsData.value.map((column) => {
    const style: Record<string, string> = getColumnWidthStyle(column)

    if (column.align) {
      style['text-align'] = column.align
    }

    return {
      ...column,
      style: Object.entries(style)
        .map(([key, value]) => `${key}: ${value}`)
        .join(';'),
    }
  }),
)

const visibleColumnsInOrder = computed<PDataTableColumnName[]>(() =>
  qColumnsData.value
    .map((column) => column.name)
    .filter((name) => !isColumnHidden(name))
    .filter((name) => visibleColumns.value === undefined || visibleColumns.value.includes(name)),
)

if (props.enablePersistence) {
  usePersistedColumns(props.enablePersistence, toRef(props, 'columns'), {
    visibleColumns,
    columnOrder,
    columnWidths,
  })
}

const resetColumnSettings = (): void => {
  columnWidths.value = {}
  columnOrder.value = [...initialColumnOrder]
  if (initialVisibleColumns !== undefined && visibleColumns.value !== undefined) {
    visibleColumns.value = [...initialVisibleColumns]
  }
}

const dataTableComponent = ref<InstanceType<typeof QTable>>()

const {
  selectedRows,
  headerCheckboxValue,
  isSelectable,
  parentCheckboxValue,
  isChildSelected,
  setChildSelected,
  onHeaderCheckboxToggle,
  onParentCheckboxToggle,
} = useSelection<T>({
  items: toRef(props, 'items'),
  rowKey: toRef(props, 'rowKey'),
  expandableItemsKey: toRef(props, 'expandableItemsKey'),
  selectable: toRef(props, 'selectable'),
  selectableChildren: toRef(props, 'selectableChildren'),
  selectedItems,
  selectedChildItems,
  isRowSelectable: toRef(props, 'isRowSelectable'),
})

const isColumnsToggleContextMenuVisible = ref<boolean>(false)
const isColumnsToggleBtnVisible = computed<boolean>(() => {
  if (!visibleColumns.value) {
    return false
  }

  return !props.disableToggleVisibleColumns
})

const nameOfColumnsWithOwnSlot = computed(() => {
  return Object.keys(slots)
    .filter((slotName) => slotName.startsWith(itemSlotKey))
    .map((slotName) => slotName.replace(itemSlotKey, ''))
})

const emitRowClick = (evt: any, row: T): void => {
  if (props.disableClickableRows || !ds.screen().isMobile) {
    return
  }

  emits('row-click', row)
}

const emitRowDblClick = (evt: any, row: T): void => {
  if (props.disableClickableRows || ds.screen().isMobile) {
    return
  }

  emits('row-click', row)
}

const qPagination = computed<TQDataTablePagination>({
  get(): TQDataTablePagination {
    return {
      sortBy: pagination.value.sortBy,
      descending: pagination.value.sortDir === SortDir.DESC,
      page: pagination.value.page,
      rowsPerPage: pagination.value.rowsPerPage ?? props.items.length,
      rowsNumber: pagination.value.rowsNumber,
    }
  },
  set(value: TQDataTablePagination): void {
    pagination.value.sortBy = value.sortBy
    pagination.value.sortDir = value.descending ? SortDir.DESC : SortDir.ASC
  },
})

const sortedItems = computed(() => {
  if (props.clientSort) {
    return useClientSort<T>(toRef(props, 'items'), qColumnsData, qPagination).value
  }

  return props.items
})

const customExpandRow = computed<boolean>(() => {
  return !!slots['customExpandRow']
})

const getChildCellValue = (col: { name: string }, row: T) => {
  const original = props.columns.find((c) => c.name === col.name)
  if (!original) {
    return row[col.name]
  }

  if (typeof original.field === 'function') {
    return original.field(row)
  }

  return row[original.field ?? original.name]
}

const hasExpandableContent = (row: T): boolean => {
  if (customExpandRow.value) {
    return props.isRowExpandable ? props.isRowExpandable(row) : true
  }

  if (!props.expandableItemsKey) {
    return false
  }

  const children = (row[props.expandableItemsKey] ?? []) as T[]

  return children.length > 0
}

const hasExpandSelectColumn = computed<boolean>(() => {
  return props.expandable || customExpandRow.value || props.selectable || props.selectableChildren
})

const customExpandRowColSpan = computed<number>(() => {
  let colSpan = props.columns.length

  if (hasExpandSelectColumn.value) {
    colSpan += 1
  }

  if (!props.disableToggleVisibleColumns || slots['item.actions']) {
    colSpan += 1
  }

  return colSpan
})

const emitRequest = (qRequest: TQDataTableRequest) => {
  qPagination.value = { ...qRequest.pagination }

  if (props.clientSort) {
    return
  }

  const request: PDataTableRequest = {
    pagination: {
      sortBy: qRequest.pagination.sortBy,
      sortDir: qRequest.pagination.descending ? SortDir.DESC : SortDir.ASC,
      page: qRequest.pagination.page,
      rowsPerPage: qRequest.pagination.rowsPerPage,
      rowsNumber: pagination.value.rowsNumber,
    },
    getCellValue: qRequest.getCellValue,
    filter: qRequest.filter,
  }

  emits('request', request)
}

const scrollReset = (): void => {
  const dataTableScrollArea = dataTableComponent.value?.$el.querySelector('.scroll')
  if (dataTableScrollArea) {
    dataTableScrollArea.scrollTop = 0
  }

  mobileScrollSync.reset()
}

const getTopmostRowElement = (rowIds: PDataTableRowIdValue[]): HTMLElement | null => {
  const rowKeys = rowIds.map(String)
  const rowElements: HTMLElement[] = Array.from(
    dataTableComponent.value?.$el.querySelectorAll('[data-row-key]') ?? [],
  )

  for (const rowElement of rowElements) {
    if (rowKeys.includes(rowElement.dataset.rowKey ?? '')) {
      return rowElement
    }
  }

  return null
}

const scrollToRows = (rowIds: PDataTableRowIdValue[]): void => {
  getTopmostRowElement(rowIds)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const requestServerInteraction = () => {
  if (props.clientSort) {
    scrollReset()
    return
  }

  dataTableComponent.value?.requestServerInteraction()
  scrollReset()
}

const showColumnsToggleContextMenu = (): void => {
  isColumnsToggleContextMenuVisible.value = !isColumnsToggleContextMenuVisible.value
}

const columnMobileWidth = (name: string): string => {
  return props.columns.find((col) => col.name === name)?.widthMobile || '120px'
}

const firstContentColumnName = (cols: { name: string }[]): string | null => {
  for (const col of cols) {
    if (!isColumnHidden(col.name)) {
      return col.name
    }
  }

  return null
}

const setRowExpansion = (scope: TQDataTableBody<T>, row: T, expanded: boolean): void => {
  if (scope.expand === expanded) {
    return
  }

  scope.expand = expanded

  if (expanded) {
    emits('expand', row)
    return
  }

  emits('collapse', row)
}

const onToggleRowExpansion = (scope: TQDataTableBody<T>, row: T): void => {
  setRowExpansion(scope, row, !scope.expand)
}

const collapseAllExpandedRows = (): void => {
  expandableItems.value = []
}

const expandAllCollapsedRows = (): void => {
  expandableItems.value = props.items.map((row) => row[props.rowKey] as PDataTableRowIdValue)
}

const expandRow = (rowId: PDataTableRowIdValue): void => {
  if (dataTableComponent.value!.isRowExpanded(rowId)) {
    return
  }

  dataTableComponent.value!.setExpanded([rowId])
}

const toggleRow = (rowId: PDataTableRowIdValue): void => {
  if (dataTableComponent.value!.isRowExpanded(rowId)) {
    expandableItems.value = expandableItems.value.filter((id) => id !== rowId)
    return
  }

  dataTableComponent.value!.setExpanded([rowId])
}

const expandRows = (rowIds: PDataTableRowIdValue[]): void => {
  dataTableComponent.value!.setExpanded(rowIds)
}

watch(
  () => qPagination.value.page,
  () => requestServerInteraction(),
)

watch(
  () => qPagination.value.rowsPerPage,
  () => requestServerInteraction(),
)

defineExpose({
  requestServerInteraction,
  scrollToRows,
  showColumnsToggleContextMenu,
  collapseAllExpandedRows,
  expandAllCollapsedRows,
  expandRows,
  expandRow,
  toggleRow,
  resetColumnSettings,
  visibleColumnsInOrder,
})
</script>

<template>
  <div
    class="ds-data-table p-flex p-flex-flow-column"
    :class="{
      'p-full-height p-justify-between': !shrink,
      'ds-data-table--custom-widths': hasCustomColumnWidths,
    }"
  >
    <q-linear-progress
      v-if="mobileLayoutEnabled && loading"
      class="ds-data-table-loader-mobile"
      size="2px"
      query
      reverse
      dark
    />

    <q-table
      ref="dataTableComponent"
      v-model:pagination="qPagination"
      v-model:selected="selectedRows"
      v-model:visible-columns="visibleColumns"
      v-model:expanded="expandableItems"
      :class="{
        'ds-data-table-sticky-first-column': stickyFirstColumn,
        'ds-data-table-sticky-last-column': stickyLastColumn,
        'ds-data-table-height-with-sticky-header': sortedItems.length !== 0,
        'ds-data-table-mobile': mobileLayoutEnabled,
        'ds-data-table--resizing': isResizing,
      }"
      class="ds-data-table ds-data-table-sticky text-grey600"
      :columns="qColumns"
      :rows="sortedItems"
      :row-key="rowKey"
      hide-pagination
      :selection="selectable ? 'multiple' : undefined"
      :separator="separator"
      :hide-header="mobileLayoutEnabled || hideHeader"
      :color="Color.PRIMARY"
      binary-state-sort
      hide-selected-banner
      flat
      @row-click="emitRowClick"
      @row-dblclick="emitRowDblClick"
      @request="emitRequest"
    >
      <template #header="scope: TQDataTableHeader">
        <tr>
          <q-th v-if="hasExpandSelectColumn" auto-width>
            <div class="ds-data-table-expand-select-cell">
              <div v-if="expandable || customExpandRow" class="ds-data-table-expand-slot" />
              <p-checkbox
                v-if="selectable"
                :model-value="headerCheckboxValue"
                cy="select-all"
                @update:model-value="onHeaderCheckboxToggle"
              />
            </div>
          </q-th>

          <template v-for="col in scope.cols" :key="col.name">
            <q-th
              v-if="!isColumnHidden(col.name)"
              :class="[
                col.__thClass,
                {
                  'ds-data-table-sticky-first-column-cell':
                    stickyFirstColumn && col.name === firstContentColumnName(scope.cols),
                  'ds-data-table-resizable-cell': isColumnResizable(col),
                },
              ]"
              :style="col.style"
              :data-column-name="col.name"
              @click="col.sortable ? scope.sort(col) : null"
            >
              <data-table-column-resize-handle
                side="left"
                :target="getResizeTargetName(scope.cols, col.name, 'left')"
                @start="startColumnResize"
              />

              <span class="ds-data-table-header-label">
                <slot
                  v-if="slots[`header.${col.name}`]"
                  :name="`header.${col.name}`"
                  :column="col"
                />
                <p-text-form-label v-else :text="col.label" />

                <i
                  v-if="col.sortable"
                  class="q-icon fa fa-light fa-chevron-up q-table__sort-icon q-table__sort-icon--left"
                  aria-hidden="true"
                  role="presentation"
                >
                </i>
              </span>

              <data-table-column-resize-handle
                side="right"
                :target="getResizeTargetName(scope.cols, col.name, 'right')"
                @start="startColumnResize"
              />
            </q-th>
          </template>

          <q-th v-if="hasCustomColumnWidths" class="ds-data-table-spacer-cell" />

          <q-th
            v-if="!disableToggleVisibleColumns || slots['item.actions']"
            class="text-right ds-data-table-actions-cell"
            auto-width
          >
            <p-btn-table
              v-if="isColumnsToggleBtnVisible"
              :color="Color.TRANSPARENT"
              icon="ellipsis-stroke-vertical"
              cy="data-table-toggle-columns-visibility-btn"
              @click="showColumnsToggleContextMenu"
            >
              <data-table-visible-columns-context-menu
                v-if="!ds.screen().isMobile"
                v-model:visible-columns="visibleColumns"
                v-model:column-order="columnOrder"
                v-model:is-open="isColumnsToggleContextMenuVisible"
                :columns="qColumns"
                @reset="resetColumnSettings"
              />
            </p-btn-table>
          </q-th>
        </tr>

        <tr v-if="loading" class="ds-data-table-loader-row">
          <th colspan="100%">
            <q-linear-progress class="ds-data-table-loader-desktop" size="2px" query reverse dark />
          </th>
        </tr>
      </template>

      <template #body="scope: TQDataTableBody<T>">
        <template v-if="mobileLayoutEnabled">
          <div
            :key="scope.key"
            :data-row-key="scope.key"
            class="ds-data-table-mobile-row"
            :class="[
              {
                'ds-data-table-stripe-even': scope.rowIndex % 2 === 0,
                'ds-data-table-stripe-odd': scope.rowIndex % 2 !== 0,
                'ds-data-table-row-highlighted': isRowHighlighted(scope.row),
              },
              getRowClass(scope.row),
            ]"
            :style="getRowStyle(scope.row)"
          >
            <data-table-item-mobile
              :ref="
                (el) => {
                  if (el) mobileScrollSync.parent.registerItem(el as ScrollableItem)
                }
              "
              :selected="parentCheckboxValue(scope)"
              :selectable="selectable"
              :select-disabled="!isSelectable(scope.row)"
              :expandable="expandable || customExpandRow"
              :hide-expand-button="!hasExpandableContent(scope.row)"
              :expanded="scope.expand"
              @scroll="mobileScrollSync.parent.onScroll"
              @row-click="emitRowClick($event, scope.row)"
              @update:selected="onParentCheckboxToggle(scope, $event)"
              @update:expanded="(value) => setRowExpansion(scope, scope.row, value)"
            >
              <template v-if="!disableToggleVisibleColumns || slots['item.actions']" #actions>
                <slot name="item.actions" :row="scope.row" :value="scope.row[rowKey]" />
              </template>

              <template #title>
                <slot name="item.mobile-title" :row="scope.row" />
              </template>

              <template v-for="(col, i) in scope.cols" :key="col.name">
                <template v-if="!isColumnHidden(col.name)">
                  <p-read-only-field
                    :label="col.label"
                    :value="col.value"
                    :style="{
                      'min-width': columnMobileWidth(col.name),
                      'max-width': columnMobileWidth(col.name),
                    }"
                  >
                    <div class="ds-data-table-mobile-field-wrapper p-flex p-items-center">
                      <template v-if="nameOfColumnsWithOwnSlot.includes(col.name)">
                        <slot
                          :name="`${itemSlotKey}${col.name}`"
                          :row="scope.row"
                          :value="col.value"
                        />
                      </template>

                      <p-text-form-body v-else :text="col.value" :max-lines="PMaxLines.ONE" />
                    </div>
                  </p-read-only-field>

                  <p-space v-if="i < scope.cols.length - 1" :mobile="PSpaceSize.LG" />
                </template>
              </template>
            </data-table-item-mobile>
          </div>

          <p-divider />

          <template v-if="expandable && scope.expand && expandableItemsKey && !customExpandRow">
            <template
              v-for="expandableRow in scope.row[expandableItemsKey] || []"
              :key="`expandable-row-mobile-${expandableRow[rowKey]}`"
            >
              <div
                :data-row-key="expandableRow[rowKey]"
                class="ds-data-table-mobile-row"
                :class="{
                  'ds-data-table-stripe-even': scope.rowIndex % 2 === 0,
                  'ds-data-table-stripe-odd': scope.rowIndex % 2 !== 0,
                  'ds-data-table-row-highlighted': isRowHighlighted(expandableRow),
                }"
              >
                <data-table-item-mobile
                  :ref="
                    (el) =>
                      mobileScrollSync.child.registerItem(
                        scope.row[rowKey] as PDataTableRowIdValue,
                        el as ScrollableItem | null,
                      )
                  "
                  :selected="isChildSelected(expandableRow[rowKey])"
                  :selectable="selectableChildren"
                  :expandable="expandable || customExpandRow"
                  hide-expand-button
                  @update:selected="
                    (e) => {
                      setChildSelected(expandableRow[rowKey], e)
                    }
                  "
                  @scroll="
                    (e) => {
                      mobileScrollSync.child.onScroll(scope.row[rowKey] as PDataTableRowIdValue, e)
                    }
                  "
                >
                  <template v-if="!disableToggleVisibleColumns || slots['item.actions']" #actions>
                    <slot
                      name="item.actions"
                      :row="expandableRow"
                      :parent-row="scope.row"
                      :value="expandableRow[rowKey]"
                    />
                  </template>

                  <template #title>
                    <slot name="item.mobile-title" :row="expandableRow" :parent-row="scope.row" />
                  </template>

                  <template v-for="(col, i) in scope.cols" :key="col.name">
                    <template v-if="!isColumnHidden(col.name)">
                      <p-read-only-field
                        class="ds-data-table-mobile-child-field"
                        :label="col.label"
                        :value="getChildCellValue(col, expandableRow)"
                        :style="{
                          'min-width': columnMobileWidth(col.name),
                          'max-width': columnMobileWidth(col.name),
                        }"
                      >
                        <div class="ds-data-table-mobile-field-wrapper p-flex p-items-center">
                          <template v-if="nameOfColumnsWithOwnSlot.includes(col.name)">
                            <slot
                              :name="`${itemSlotKey}${col.name}`"
                              :row="expandableRow"
                              :value="getChildCellValue(col, expandableRow)"
                              :parent-row="scope.row"
                            />
                          </template>

                          <p-text-form-body
                            v-else
                            :text="getChildCellValue(col, expandableRow)"
                            :max-lines="PMaxLines.ONE"
                          />
                        </div>
                      </p-read-only-field>

                      <p-space v-if="i < scope.cols.length - 1" :mobile="PSpaceSize.LG" />
                    </template>
                  </template>
                </data-table-item-mobile>
              </div>

              <p-divider />
            </template>
          </template>
        </template>

        <tr
          v-else
          :key="scope.key"
          :data-row-key="scope.key"
          :class="{
            'ds-data-table-stripe-even': scope.rowIndex % 2 === 0,
            'ds-data-table-stripe-odd': scope.rowIndex % 2 !== 0,
            'ds-data-table-row-highlighted': isRowHighlighted(scope.row),
          }"
          @click="(e) => emitRowClick(e, scope.row)"
          @dblclick="(e) => emitRowDblClick(e, scope.row)"
        >
          <q-td
            v-if="hasExpandSelectColumn"
            auto-width
            :class="getRowClass(scope.row)"
            :style="getRowStyle(scope.row)"
            @click.stop
            @dblclick.stop
          >
            <div class="ds-data-table-expand-select-cell">
              <div v-if="expandable || customExpandRow" class="ds-data-table-expand-slot">
                <p-btn-table
                  v-if="hasExpandableContent(scope.row)"
                  :icon="scope.expand ? 'minus' : 'plus'"
                  :color="Color.TRANSPARENT"
                  :cy="`expand-row-${scope.key}`"
                  @click="onToggleRowExpansion(scope, scope.row)"
                />
              </div>
              <p-checkbox
                v-if="selectable"
                :model-value="parentCheckboxValue(scope)"
                :disable="!isSelectable(scope.row)"
                :cy="`select-row-${scope.key}`"
                @update:model-value="onParentCheckboxToggle(scope, $event)"
              />
            </div>
          </q-td>

          <template v-for="col in scope.cols" :key="col.name">
            <template v-if="!isColumnHidden(col.name)">
              <q-td
                v-if="!nameOfColumnsWithOwnSlot.includes(col.name)"
                :class="[
                  col.__thClass,
                  {
                    'ds-data-table-sticky-first-column-cell':
                      stickyFirstColumn && col.name === firstContentColumnName(scope.cols),
                  },
                  getRowClass(scope.row),
                ]"
                :style="[col.style, getRowStyle(scope.row)]"
              >
                <div class="ds-data-table-cell-content">
                  <p-text-standard :text="col.value" :max-lines="PMaxLines.ONE" />
                </div>
              </q-td>
              <q-td
                v-else
                :class="[
                  col.__thClass,
                  {
                    'ds-data-table-sticky-first-column-cell':
                      stickyFirstColumn && col.name === firstContentColumnName(scope.cols),
                  },
                  getRowClass(scope.row),
                ]"
                :style="[col.style, getRowStyle(scope.row)]"
              >
                <div class="ds-data-table-cell-content">
                  <slot :name="`${itemSlotKey}${col.name}`" :row="scope.row" :value="col.value" />
                </div>
              </q-td>
            </template>
          </template>

          <q-td
            v-if="hasCustomColumnWidths"
            :class="['ds-data-table-spacer-cell', getRowClass(scope.row)]"
            :style="getRowStyle(scope.row)"
          />

          <q-td
            v-if="!disableToggleVisibleColumns || slots['item.actions']"
            auto-width
            :class="['text-right', 'ds-data-table-actions-cell', getRowClass(scope.row)]"
            :style="getRowStyle(scope.row)"
          >
            <slot name="item.actions" :row="scope.row" :value="scope.row[rowKey]" />
          </q-td>
        </tr>

        <template v-if="customExpandRow">
          <tr
            v-if="scope.expand && customExpandRowConnector && !mobileLayoutEnabled"
            class="ds-data-table-child-row ds-data-table-child-row--first ds-data-table-child-row--last"
          >
            <q-td auto-width>
              <div class="ds-data-table-expand-select-cell">
                <div class="ds-data-table-expand-slot" />
              </div>
            </q-td>
            <td :colspan="customExpandRowColSpan - 1" style="padding: 0; margin: 0">
              <slot name="customExpandRow" :row="scope.row" />
            </td>
          </tr>
          <td
            v-else-if="scope.expand"
            :colspan="customExpandRowColSpan"
            style="padding: 0; margin: 0"
          >
            <slot name="customExpandRow" :row="scope.row" />
          </td>
        </template>

        <template
          v-if="
            !mobileLayoutEnabled &&
            expandable &&
            scope.expand &&
            expandableItemsKey &&
            !customExpandRow
          "
        >
          <tr
            v-for="(expandableRow, expandableRowIndex) in scope.row[expandableItemsKey] || []"
            :key="`expandable-row-${expandableRow[rowKey]}`"
            :data-row-key="expandableRow[rowKey]"
            class="ds-data-table-child-row"
            :class="{
              'ds-data-table-stripe-even': scope.rowIndex % 2 === 0,
              'ds-data-table-stripe-odd': scope.rowIndex % 2 !== 0,
              'ds-data-table-row-highlighted': isRowHighlighted(expandableRow),
              'ds-data-table-child-row--first': expandableRowIndex === 0,
              'ds-data-table-child-row--last':
                expandableRowIndex === (scope.row[expandableItemsKey] || []).length - 1,
            }"
          >
            <q-td v-if="hasExpandSelectColumn" auto-width>
              <div class="ds-data-table-expand-select-cell">
                <div v-if="expandable || customExpandRow" class="ds-data-table-expand-slot" />
                <p-checkbox
                  v-if="selectableChildren"
                  :model-value="isChildSelected(expandableRow[rowKey])"
                  :cy="`select-child-row-${expandableRow[rowKey]}`"
                  @update:model-value="setChildSelected(expandableRow[rowKey], $event)"
                />
              </div>
            </q-td>

            <template v-for="col in scope.cols" :key="col.name">
              <q-td
                v-if="!nameOfColumnsWithOwnSlot.includes(col.name)"
                :class="[
                  col.__thClass,
                  {
                    'ds-data-table-sticky-first-column-cell':
                      stickyFirstColumn && col.name === firstContentColumnName(scope.cols),
                  },
                ]"
                :style="col.style"
              >
                <div class="ds-data-table-cell-content">
                  <p-text-standard
                    :text="getChildCellValue(col, expandableRow)"
                    :max-lines="PMaxLines.ONE"
                  />
                </div>
              </q-td>
              <q-td
                v-else
                :class="[
                  col.__thClass,
                  {
                    'ds-data-table-sticky-first-column-cell':
                      stickyFirstColumn && col.name === firstContentColumnName(scope.cols),
                  },
                ]"
                :style="col.style"
              >
                <div class="ds-data-table-cell-content">
                  <slot
                    :name="`${itemSlotKey}${col.name}`"
                    :row="expandableRow"
                    :value="getChildCellValue(col, expandableRow)"
                    :parent-row="scope.row"
                  />
                </div>
              </q-td>
            </template>

            <q-td v-if="hasCustomColumnWidths" class="ds-data-table-spacer-cell" />

            <q-td
              v-if="!disableToggleVisibleColumns || slots['item.actions']"
              class="text-right ds-data-table-actions-cell"
            >
              <slot name="item.actions" :row="expandableRow" :parent-row="scope.row" />
            </q-td>
          </tr>
        </template>
      </template>

      <template #no-data>
        <div v-if="!loading" class="p-flex p-flex-center p-full-width ds-data-table-empty">
          <p-text-standard
            :text="noData || $t('No matching records found')"
            :color="Color.GREY_500"
          />
        </div>
      </template>

      <slot />
    </q-table>

    <div>
      <p-data-table-pagination v-if="!hidePagination" v-model="pagination" :disabled="loading" />
    </div>

    <data-table-visible-columns-context-menu
      v-if="ds.screen().isMobile"
      v-model:visible-columns="visibleColumns"
      v-model:column-order="columnOrder"
      v-model:is-open="isColumnsToggleContextMenuVisible"
      :columns="qColumns"
      @reset="resetColumnSettings"
    />
  </div>
</template>

<style lang="scss">
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/brand.module';

$cell-height: 40px;
$mobile-cell-height: 40px;

.ds-data-table {
  max-width: 100%;
  position: relative;

  thead th {
    p {
      display: inline;
    }

    border-bottom: 1px solid getColor('grey300');

    .ds-data-table-header-label {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      max-width: 100%;
      min-width: 0;
      vertical-align: middle;

      p {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
        flex: 0 1 auto;
      }

      i {
        flex-shrink: 0;
      }
    }
  }

  .ds-data-table-cell-content {
    width: 0;
    min-width: 100%;
    overflow: hidden;

    > p {
      text-align: inherit;
    }
  }

  .ds-data-table-column-resizer {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 6px;
    cursor: col-resize;
    user-select: none;
    touch-action: none;
    z-index: 4;
    pointer-events: none;

    &::after {
      content: '';
      position: absolute;
      top: 10px;
      bottom: 10px;
      width: 1px;
      background-color: transparent;
      transition: background-color 120ms ease-in-out;
      pointer-events: none;
    }

    &--right {
      right: 0;

      &::after {
        right: 0;
      }
    }

    &--left {
      left: 0;

      &::after {
        left: -1px;
      }
    }
  }

  .ds-data-table-resizable-cell:hover .ds-data-table-column-resizer {
    pointer-events: auto;

    &::after {
      background-color: getColor('grey300');
    }
  }

  &.ds-data-table--resizing {
    .ds-data-table-column-resizer {
      pointer-events: auto;
    }

    .ds-data-table-column-resizer::after {
      background-color: transparent;
    }
  }

  &.ds-data-table--custom-widths {
    .ds-data-table-spacer-cell {
      width: auto;
      padding: 0;
    }

    thead th.ds-data-table-spacer-cell {
      background: #ffffff;

      &:hover {
        border-bottom-color: getColor('grey300');
      }
    }

    .ds-data-table-actions-cell {
      position: sticky;
      right: 0;
    }

    thead th.ds-data-table-actions-cell {
      background-color: #ffffff;
      z-index: 3;
    }

    tbody td.ds-data-table-actions-cell {
      z-index: 1;
    }
  }

  thead tr {
    height: $cell-height;
  }

  tbody td {
    height: $cell-height;
    border-bottom: 1px solid getColor('grey100');
  }

  tbody tr.ds-data-table-row-highlighted td {
    box-shadow: inset 0 0 0 9999px getColor('note');
  }

  $expand-slot-width: 28px;
  $expand-select-gap: 4px;
  $tree-line-offset: calc($expand-slot-width / 2);

  .ds-data-table-expand-select-cell {
    display: flex;
    align-items: center;
    gap: $expand-select-gap;
  }

  .ds-data-table-expand-slot {
    width: $expand-slot-width;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:not(.ds-data-table-mobile) {
    .ds-data-table-child-row {
      > td:first-child {
        position: relative;
        overflow: visible;

        .ds-data-table-expand-select-cell {
          position: relative;
          min-height: $cell-height;

          &::before {
            content: '';
            position: absolute;
            left: $tree-line-offset;
            top: -50%;
            bottom: -50%;
            width: 1px;
            background-color: getColor('grey300');
            pointer-events: none;
          }

          &::after {
            content: '';
            position: absolute;
            left: $tree-line-offset;
            width: $tree-line-offset;
            top: 50%;
            height: 1px;
            background-color: getColor('grey300');
            pointer-events: none;
          }
        }
      }

      &--first > td:first-child .ds-data-table-expand-select-cell::before {
        top: 0;
      }

      &--last > td:first-child .ds-data-table-expand-select-cell::before {
        bottom: 50%;
      }
    }
  }

  &-loader {
    &-mobile {
      position: absolute !important;
      z-index: 1;
    }

    &-desktop {
      position: absolute !important;
      top: -2px;
      left: 0;
    }

    &-row {
      height: auto !important;

      th {
        padding: 0;
        border: none !important;
      }
    }
  }

  &-mobile {
    border-radius: 0;

    table {
      display: block;

      tbody {
        display: block;
      }
    }

    &-child-field {
      margin-left: 25px;
    }
  }

  &-sticky {
    tr th {
      position: sticky;
      z-index: 2;
      background: #ffffff;
    }

    &-header {
      thead tr:last-child th {
        top: $cell-height;
        z-index: 3;
      }

      thead tr:first-child th {
        top: 0;
        z-index: 1;
      }
    }

    &-first-column {
      .ds-data-table-sticky-first-column-cell {
        position: sticky;
        left: 0;
      }

      th.ds-data-table-sticky-first-column-cell {
        z-index: 3;
      }

      td.ds-data-table-sticky-first-column-cell {
        z-index: 1;
      }
    }

    &-last-column {
      tr:last-child th:last-child {
        z-index: 3;
      }

      td:last-child {
        z-index: 1;
      }

      td:last-child,
      th:last-child {
        position: sticky;
        right: 0;
      }
    }
  }
}

.ds-data-table-height-with-sticky-header {
  min-height: 0; // flex fix for vertical scroll

  .q-table__top,
  .q-table__bottom,
  thead tr:first-child th {
    background-color: #ffffff;
  }

  thead tr th {
    position: sticky;
  }

  /* this will be the loading indicator */
  thead tr:last-child th {
    /* height of all previous header rows */
    top: $cell-height;
  }

  thead tr:first-child th {
    top: 0;
  }

  /* prevent scrolling behind sticky top row on focus */
  tbody {
    /* height of all previous header rows */
    scroll-margin-top: $cell-height;
  }
}

.ds-data-table-mobile-field-wrapper {
  height: $mobile-cell-height;
}

.ds-data-table-mobile-row.ds-data-table-row-highlighted {
  box-shadow: inset 0 0 0 9999px getColor('note');
}
</style>
